import { useCallback, useEffect } from 'react';

import { useTracking } from '@xing-com/crate-communication-tracking';

const TRACKING_ELEMENT_DETAIL = 'consent_pay_wall_overlay';
const TRACKING_SENT_BY = 'platform';
const LOGIN_SIGNUP_CHANNEL = 'minireg_consent_pay_wall';

type PayOrConsentTrackingEvent = 'clicked' | 'opened' | 'viewed';

type PayOrConsentTrackingElement =
  | 'consent_accept'
  | 'get_premium'
  | 'information_expand'
  | 'log_in'
  | 'overlay'
  | 'xing_privacy';

type TrackPayOrConsentEvent = {
  element: PayOrConsentTrackingElement;
  event: PayOrConsentTrackingEvent;
};

type UsePayOrConsentTrackingResult = {
  getTrackedLoginUrl: (loginUrl: string) => string;
  trackConsentAccept: () => void;
  trackGetPremium: () => void;
  trackInformationExpand: () => void;
  trackLogIn: () => void;
  trackXingPrivacy: () => void;
};

const appendQueryParameter = (
  url: string,
  name: string,
  value: string
): string => {
  const separator = url.includes('?') ? '&' : '?';

  return `${url}${separator}${name}=${value}`;
};

export const usePayOrConsentTracking = (): UsePayOrConsentTrackingResult => {
  const { track } = useTracking();

  const trackPayOrConsentEvent = useCallback(
    ({ element, event }: TrackPayOrConsentEvent): void => {
      track({
        type: 'nwt',
        event,
        eventSchema: 'basic',
        sentBy: TRACKING_SENT_BY,
        element,
        elementDetail: TRACKING_ELEMENT_DETAIL,
      });
    },
    [track]
  );

  useEffect(() => {
    trackPayOrConsentEvent({
      element: 'overlay',
      event: 'viewed',
    });
  }, [trackPayOrConsentEvent]);

  return {
    getTrackedLoginUrl: (loginUrl) =>
      appendQueryParameter(loginUrl, 'signup_channel', LOGIN_SIGNUP_CHANNEL),
    trackConsentAccept: () =>
      trackPayOrConsentEvent({
        element: 'consent_accept',
        event: 'opened',
      }),
    trackGetPremium: () =>
      trackPayOrConsentEvent({
        element: 'get_premium',
        event: 'opened',
      }),
    trackInformationExpand: () =>
      trackPayOrConsentEvent({
        element: 'information_expand',
        event: 'clicked',
      }),
    trackLogIn: () =>
      trackPayOrConsentEvent({
        element: 'log_in',
        event: 'opened',
      }),
    trackXingPrivacy: () =>
      trackPayOrConsentEvent({
        element: 'xing_privacy',
        event: 'opened',
      }),
  };
};
